import React, { useState } from 'react';
import { Link, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useClubSettings } from '../../contexts/ClubSettingsContext';
import { clubLogoSrc, handleClubLogoError } from '../../utils/clubLogo';
import BrandLockup from '../ui/BrandLockup';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { usePageTitle } from '../../hooks/usePageTitle';

/**
 * Csak app-on belüli útvonalra irányítunk át. A "//host" alakú cím a böngészőben
 * idegen oldalra vinne, ezért azt is kizárjuk.
 */
const isSafeInternalPath = (path: string | null | undefined): path is string =>
  !!path && path.startsWith('/') && !path.startsWith('//');

const LoginForm: React.FC = () => {
  usePageTitle('Bejelentkezés');
  const { login } = useAuth();
  const { settings: clubSettings } = useClubSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  /**
   * Hová megyünk a sikeres belépés után: ahonnan ide dobtak minket. Az állapot az
   * elsődleges; a query paramétert csak a korábbi verzióból megmaradt linkek
   * miatt olvassuk még - újat már nem gyártunk belőle.
   */
  const resolveTarget = (): string => {
    const fromState = (location.state as { from?: string } | null)?.from;
    if (isSafeInternalPath(fromState)) {
      return fromState;
    }

    const legacy = searchParams.get('redirect');
    return isSafeInternalPath(legacy) ? legacy : '/';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await login(email, password);
      navigate(resolveTarget());
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        {/* A belépőn a klubé az elsőbbség: a saját címere és neve fogadja a
            felhasználót. Klubnév híján a Portero lockup áll a helyén - így a
            képernyő sosem marad névtelen "Bejelentkezés" felirattal. */}
        <div>
          {clubSettings?.clubName ? (
            <>
              <div className="flex justify-center mb-4">
                <img
                  src={clubLogoSrc(clubSettings.crestUrl)}
                  alt=""
                  className="h-20 w-20 object-contain"
                  onError={handleClubLogoError}
                />
              </div>
              <h2 className="text-center text-3xl font-extrabold text-gray-900">
                {clubSettings.clubName}
              </h2>
            </>
          ) : (
            <h2 className="flex justify-center">
              <BrandLockup size="lg" />
            </h2>
          )}
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <div className="text-sm text-red-700">{error}</div>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="sr-only">
                Email cím
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                placeholder="Email cím"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div className="relative">
              <label htmlFor="password" className="sr-only">
                Jelszó
              </label>
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                className="appearance-none relative block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-lg focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                placeholder="Jelszó"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Jelszó elrejtése' : 'Jelszó megjelenítése'}
              >
                {showPassword ? (
                  <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                ) : (
                  <EyeIcon className="h-5 w-5 text-gray-400" />
                )}
              </button>
            </div>
          </div>

          <div className="-mt-2 text-right text-sm">
            <Link
              to="/forgot-password"
              state={{ email }}
              className="font-medium text-primary-600 hover:text-primary-500"
            >
              Elfelejtetted a jelszavad?
            </Link>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Bejelentkezés...' : 'Bejelentkezés'}
            </button>
          </div>

          <p className="text-center text-sm text-gray-600">
            Nincs még fiókod?{' '}
            <Link to="/register" className="font-medium text-primary-600 hover:text-primary-500">
              Regisztrálok
            </Link>
          </p>
        </form>

        {/* Halk gyártói aláírás a klub saját arculata alatt. Csak akkor, ha a
            klub neve viszi a fejlécet - máskülönben a lockup már ott van. */}
        {clubSettings?.clubName && (
          <p className="text-center text-xs text-gray-400">
            Működteti a <span className="wordmark text-[13px] text-gray-500">Portero</span>
          </p>
        )}
      </div>
    </div>
  );
};

export default LoginForm;